import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { vendorService } from '../../../services/api';
import DateRangePicker from '../../../components/DateRangePicker';

const OverviewTab = ({ toast, setActiveTab, setListingSubTab }) => {
  const { hasPermission } = useAuth();

  // Filters State
  const [overviewPreset, setOverviewPreset] = useState('all');
  const [overviewStartDate, setOverviewStartDate] = useState('');
  const [overviewEndDate, setOverviewEndDate] = useState('');

  // Data States
  const [listings, setListings] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [manualMemberships, setManualMemberships] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);

  // UI Interactive States
  const [hoveredRevenueIndex, setHoveredRevenueIndex] = useState(null);
  const [hoveredListingIndex, setHoveredListingIndex] = useState(null);
  const [hoveredSignUpIndex, setHoveredSignUpIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch Data on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await vendorService.getAdminOverviewStats();
        if (res.success && res.data) {
          setListings(res.data.listings || []);
          setMemberships(res.data.memberships || []);
          setManualMemberships(res.data.manual_memberships || []);
          setTickets(res.data.tickets || []);
          setUsers(res.data.users || []);
        } else {
          setError(res.error || 'Failed to fetch overview metrics data.');
        }
      } catch (err) {
        setError('Failed to fetch overview metrics data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Date utilities
  const formatDateToYYYYMMDD = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const parseListingDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr.includes('T') || (dateStr.includes('-') && dateStr.indexOf('-') === 4)) {
      return new Date(dateStr);
    }
    try {
      const parts = dateStr.split(' ');
      const dateParts = parts[0].split('-');
      const day = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1;
      const year = parseInt(dateParts[2], 10);
      return new Date(year, month, day);
    } catch (e) {
      return new Date(dateStr);
    }
  };

  const handleOverviewPreset = (preset) => {
    const today = new Date();
    let start = '';
    let end = '';

    if (preset === '7days') {
      const d = new Date();
      d.setDate(today.getDate() - 6);
      start = formatDateToYYYYMMDD(d);
      end = formatDateToYYYYMMDD(today);
    } else if (preset === '30days') {
      const d = new Date();
      d.setDate(today.getDate() - 29);
      start = formatDateToYYYYMMDD(d);
      end = formatDateToYYYYMMDD(today);
    } else if (preset === 'thismonth') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      start = formatDateToYYYYMMDD(firstDay);
      end = formatDateToYYYYMMDD(today);
    } else if (preset === '6months') {
      const d = new Date(today.getFullYear(), today.getMonth() - 5, 1);
      start = formatDateToYYYYMMDD(d);
      end = formatDateToYYYYMMDD(today);
    } else if (preset === 'all') {
      start = '';
      end = '';
    }

    setOverviewStartDate(start);
    setOverviewEndDate(end);
    setOverviewPreset(preset);
  };

  // Dynamic filter logic
  const checkDateInRange = (dateStr) => {
    if (!dateStr) return true;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return true;
    if (overviewStartDate) {
      const start = new Date(overviewStartDate);
      start.setHours(0, 0, 0, 0);
      if (date < start) return false;
    }
    if (overviewEndDate) {
      const end = new Date(overviewEndDate);
      end.setHours(23, 59, 59, 999);
      if (date > end) return false;
    }
    return true;
  };

  const checkListingDateInRange = (dateStr) => {
    if (!dateStr) return true;
    const lDate = parseListingDate(dateStr);
    if (!lDate || isNaN(lDate.getTime())) return true;
    if (overviewStartDate) {
      const start = new Date(overviewStartDate);
      start.setHours(0, 0, 0, 0);
      if (lDate < start) return false;
    }
    if (overviewEndDate) {
      const end = new Date(overviewEndDate);
      end.setHours(23, 59, 59, 999);
      if (lDate > end) return false;
    }
    return true;
  };

  const filteredUsersForOverview = users.filter(u => checkDateInRange(u.createdAt || u.created_at));
  const filteredListingsForOverview = listings.filter(l => checkListingDateInRange(l.created_at || l.createdAt));
  const filteredMembershipsForOverview = memberships.filter(m => checkDateInRange(m.created_at));
  const filteredManualMembershipsForOverview = manualMemberships.filter(m => checkDateInRange(m.start_date));
  const filteredTicketsForOverview = tickets.filter(t => checkDateInRange(t.created_at || t.createdAt));

  // Chart aggregation logic
  const getTrendIntervals = () => {
    let start, end;
    if (overviewStartDate && overviewEndDate) {
      let startD = new Date(overviewStartDate);
      let endD = new Date(overviewEndDate);
      if (startD > endD) {
        const temp = startD;
        startD = endD;
        endD = temp;
      }
      start = startD;
      start.setHours(0, 0, 0, 0);
      end = endD;
      end.setHours(23, 59, 59, 999);
    } else {
      const now = new Date();
      start = new Date(now.getFullYear(), now.getMonth() - 5, 1);
      start.setHours(0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      end.setHours(23, 59, 59, 999);
    }

    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const intervals = [];

    if (diffDays <= 31) {
      const current = new Date(start);
      while (current <= end) {
        const dayStart = new Date(current);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(current);
        dayEnd.setHours(23, 59, 59, 999);
        intervals.push({
          name: current.toLocaleDateString('default', { day: '2-digit', month: 'short' }),
          year: current.getFullYear(),
          monthIndex: current.getMonth(),
          start: dayStart,
          end: dayEnd,
          revenue: 0,
          count: 0
        });
        current.setDate(current.getDate() + 1);
      }
    } else {
      const current = new Date(start.getFullYear(), start.getMonth(), 1);
      const targetEnd = new Date(end.getFullYear(), end.getMonth(), 1);
      while (current <= targetEnd) {
        const monthStart = new Date(current.getFullYear(), current.getMonth(), 1);
        monthStart.setHours(0, 0, 0, 0);
        const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0);
        monthEnd.setHours(23, 59, 59, 999);

        const intervalStart = monthStart < start ? start : monthStart;
        const intervalEnd = monthEnd > end ? end : monthEnd;

        intervals.push({
          name: current.toLocaleString('default', { month: 'short' }) + (current.getFullYear() !== new Date().getFullYear() ? ` '${String(current.getFullYear()).slice(-2)}` : ''),
          year: current.getFullYear(),
          monthIndex: current.getMonth(),
          start: intervalStart,
          end: intervalEnd,
          revenue: 0,
          count: 0
        });
        current.setMonth(current.getMonth() + 1);
      }
    }
    return intervals;
  };

  const getRevenueTrendData = () => {
    const intervals = getTrendIntervals();
    // Only process online/gateway memberships (where !m.is_manual)
    filteredMembershipsForOverview.filter(m => !m.is_manual).forEach(m => {
      if (!m.created_at || (m.plan_name || '').toUpperCase() === 'FREE') return;
      const mDate = new Date(m.created_at);
      intervals.forEach(interval => {
        if (mDate >= interval.start && mDate <= interval.end) {
          interval.revenue += Number(m.amount) || 0;
          interval.count += 1;
        }
      });
    });

    // Process manual memberships
    filteredManualMembershipsForOverview.forEach(m => {
      if (!m.start_date || (m.plan_name || '').toUpperCase() === 'FREE') return;
      const mDate = new Date(m.start_date);
      intervals.forEach(interval => {
        if (mDate >= interval.start && mDate <= interval.end) {
          interval.revenue += Number(m.amount) || 0;
          interval.count += 1;
        }
      });
    });
    return intervals;
  };

  const getListingTrendData = () => {
    const intervals = getTrendIntervals();
    filteredListingsForOverview.forEach(l => {
      const dateStr = l.created_at || l.createdAt;
      if (!dateStr) return;
      const lDate = parseListingDate(dateStr);
      if (!lDate || isNaN(lDate.getTime())) return;
      intervals.forEach(interval => {
        if (lDate >= interval.start && lDate <= interval.end) {
          interval.count += 1;
        }
      });
    });
    return intervals;
  };

  const getUserSignUpTrendData = () => {
    const intervals = getTrendIntervals();
    filteredUsersForOverview.forEach(u => {
      const dateStr = u.createdAt || u.created_at;
      if (!dateStr) return;
      const uDate = new Date(dateStr);
      if (isNaN(uDate.getTime())) return;
      intervals.forEach(interval => {
        if (uDate >= interval.start && uDate <= interval.end) {
          interval.count += 1;
        }
      });
    });
    return intervals;
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center py-24 select-none">
        <div className="w-9 h-9 border-4 border-[#2ab391] border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-3 text-sm text-gray-500 font-semibold mt-3">Loading overview stats...</span>
      </div>
    );
  }

  return (
    <div className="transition-all duration-300">
      {error && (
        <div className="bg-red-50 text-red-700 border border-red-200/50 p-4 rounded-xl mb-6 flex items-center gap-3 shadow-sm text-left">
          <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      {/* Date Range Filter Panel */}
      <div className="bg-white border border-gray-200/80 rounded-2xl p-5 mb-8 shadow-xs flex flex-col xl:flex-row xl:items-center justify-between gap-5 select-none">
        <div className="flex flex-col gap-1 text-left">
          <h2 className="text-base font-extrabold text-gray-800">Overview Filter</h2>
          <p className="text-xs text-gray-400 font-semibold">Select custom dates or presets to filter all stats and charts</p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="flex flex-wrap bg-slate-50 border border-slate-100 rounded-xl p-1 gap-1">
            {[
              { id: '7days', label: '7 Days' },
              { id: '30days', label: '30 Days' },
              { id: 'thismonth', label: 'This Month' },
              { id: '6months', label: '6 Months' },
              { id: 'all', label: 'All Time' }
            ].map(opt => (
              <button
                key={opt.id}
                type="button"
                onClick={() => handleOverviewPreset(opt.id)}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-extrabold uppercase tracking-wider transition-all cursor-pointer ${overviewPreset === opt.id
                  ? 'bg-[#2ab391] text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100/50'
                  }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <DateRangePicker
              startDate={overviewStartDate}
              endDate={overviewEndDate}
              onChange={(start, end) => {
                setOverviewStartDate(start);
                setOverviewEndDate(end);
                setOverviewPreset(start || end ? 'custom' : 'all');
              }}
              placeholder="Select Date Range"
              className="w-56 bg-slate-50/50 hover:bg-slate-50 border-gray-300"
            />
            {(overviewStartDate || overviewEndDate) && (
              <button
                type="button"
                onClick={() => handleOverviewPreset('all')}
                className="text-[10px] font-extrabold text-rose-500 hover:text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-xl px-3 py-2 transition-all cursor-pointer ml-1"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 mb-8">
        {/* Card 1: System Users */}
        {hasPermission('MANAGE_USERS') && (() => {
          const adminCount = filteredUsersForOverview.filter(u =>
            u.roles && u.roles.some(role =>
              ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_SALES_ADMIN', 'ROLE_LISTING_ADMIN', 'ROLE_SUPPORT_ADMIN', 'ROLE_MAGAZINE_SALES'].includes(role)
            )
          ).length;
          const vendorCount = filteredUsersForOverview.filter(u =>
            !u.roles || !u.roles.some(role =>
              ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_SALES_ADMIN', 'ROLE_LISTING_ADMIN', 'ROLE_SUPPORT_ADMIN', 'ROLE_MAGAZINE_SALES'].includes(role)
            )
          ).length;
          return (
            <div
              onClick={() => setActiveTab('users')}
              className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex flex-col justify-between hover:shadow-md cursor-pointer hover:scale-[1.02] active:scale-[0.99] transition-all min-h-[140px]"
            >
              <div className="flex items-center justify-between w-full">
                <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <div className="mt-4 text-left">
                <h3 className="text-2xl font-bold text-gray-800 tracking-tight text-left">{filteredUsersForOverview.length}</h3>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-1 text-left">System Users</p>
                <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between text-[9px] font-bold text-slate-400">
                  <span>Admins: <span className="text-gray-700">{adminCount}</span></span>
                  <span>Vendors: <span className="text-gray-700">{vendorCount}</span></span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Card 2: Listings Overview */}
        {hasPermission('APPROVE_LISTINGS') && (() => {
          const totalListings = filteredListingsForOverview.length;
          const pendingListings = filteredListingsForOverview.filter(l => l.status === 'PENDING').length;
          const approvedClaimedListingsCount = filteredListingsForOverview.filter(l => l.status === 'APPROVED' && l.isClaimed).length;
          const rejectedListings = filteredListingsForOverview.filter(l => l.status === 'REJECTED').length;
          return (
            <div
              onClick={() => { setActiveTab('listings'); setListingSubTab('new'); }}
              className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex flex-col justify-between hover:shadow-md cursor-pointer hover:scale-[1.02] active:scale-[0.99] transition-all min-h-[140px]"
            >
              <div className="flex items-center justify-between w-full">
                <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                {pendingListings > 0 && (
                  <span className="flex h-2 w-2 relative">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                  </span>
                )}
              </div>
              <div className="mt-4 text-left">
                <h3 className="text-2xl font-bold text-gray-800 tracking-tight text-left">{approvedClaimedListingsCount}</h3>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-1 text-left">Approved Listings</p>
                <span className="text-[9px] text-gray-400 font-medium block -mt-0.5 mb-1 text-left">(without unclaimed)</span>
                <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between gap-1 text-[9px] font-bold text-slate-400">
                  <span>New: <span className="text-gray-700">{pendingListings}</span></span>
                  <span>Total: <span className="text-gray-700">{totalListings}</span></span>
                  <span>Rej: <span className="text-gray-700">{rejectedListings}</span></span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Card 3: Paid Memberships */}
        {hasPermission('MANAGE_MEMBERSHIPS') && (() => {
          const gatewayMemberships = filteredMembershipsForOverview.filter(m => !m.is_manual);
          const activeGatewayPaid = gatewayMemberships.filter(m => (m.plan_name || '').toUpperCase() !== 'FREE' && (m.status || '').toUpperCase() === 'ACTIVE');
          const activeManualPaid = filteredManualMembershipsForOverview.filter(m => !m.is_expired);
          const totalPaidPeopleCount = activeGatewayPaid.length + activeManualPaid.length;

          const gatewayRev = gatewayMemberships.filter(m => (m.plan_name || '').toUpperCase() !== 'FREE').reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
          const manualRev = filteredManualMembershipsForOverview.reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
          return (
            <div
              onClick={() => setActiveTab('memberships_audit')}
              className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex flex-col justify-between hover:shadow-md cursor-pointer hover:scale-[1.02] active:scale-[0.99] transition-all min-h-[140px]"
            >
              <div className="flex items-center justify-between w-full">
                <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
              </div>
              <div className="mt-4 text-left">
                <h3 className="text-2xl font-bold text-gray-800 tracking-tight text-left">{totalPaidPeopleCount}</h3>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-1 text-left">Paid Memberships</p>
                <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between gap-1 text-[9px] font-bold text-slate-400">
                  <span>Gateway Rev: <span className="text-gray-700">₹{gatewayRev.toLocaleString()}</span></span>
                  <span>Manual Rev: <span className="text-gray-700">₹{manualRev.toLocaleString()}</span></span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Card 4: Total Sales Revenue */}
        {hasPermission('MANAGE_MEMBERSHIPS') && (() => {
          const gatewayMemberships = filteredMembershipsForOverview.filter(m => !m.is_manual);
          const gatewayRev = gatewayMemberships.filter(m => (m.plan_name || '').toUpperCase() !== 'FREE').reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
          const manualRev = filteredManualMembershipsForOverview.reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
          const totalRev = gatewayRev + manualRev;
          return (
            <div
              onClick={() => setActiveTab('memberships_audit')}
              className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex flex-col justify-between hover:shadow-md cursor-pointer hover:scale-[1.02] active:scale-[0.99] transition-all min-h-[140px]"
            >
              <div className="flex items-center justify-between w-full">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                {manualRev > 0 && (
                  <span className="text-[9px] font-extrabold text-indigo-600 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded">
                    Incl. Manual
                  </span>
                )}
              </div>
              <div className="mt-4 text-left">
                <h3 className="text-2xl font-bold text-gray-800 tracking-tight text-left">₹{totalRev.toLocaleString()}</h3>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-1 text-left">Total Sales Revenue</p>
                <div className="mt-2.5 pt-2 border-t border-slate-100 flex flex-col gap-1.5 text-[9px] font-bold text-slate-400">
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-1 font-semibold">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block"></span>
                      Gateway
                    </span>
                    <span className="text-gray-700">₹{gatewayRev.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-1 font-semibold">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 inline-block"></span>
                      Manual
                    </span>
                    <span className="text-indigo-600">₹{manualRev.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Card 5: Open Tickets */}
        {hasPermission('RESOLVE_ISSUES') && (() => {
          const openCount = filteredTicketsForOverview.filter(t => t.status === 'PENDING' || t.status === 'IN_PROGRESS').length;
          const pCount = filteredTicketsForOverview.filter(t => t.status === 'PENDING').length;
          const iCount = filteredTicketsForOverview.filter(t => t.status === 'IN_PROGRESS').length;
          const sCount = filteredTicketsForOverview.filter(t => t.status === 'SOLVED').length;
          return (
            <div
              onClick={() => setActiveTab('support')}
              className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex flex-col justify-between hover:shadow-md cursor-pointer hover:scale-[1.02] active:scale-[0.99] transition-all min-h-[140px]"
            >
              <div className="flex items-center justify-between w-full">
                <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
              </div>
              <div className="mt-4 text-left">
                <h3 className="text-2xl font-bold text-gray-800 tracking-tight text-left">{openCount}</h3>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-1 text-left">Open Tickets</p>
                <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between gap-1 text-[9px] font-bold text-slate-400">
                  <span className="text-amber-600 bg-amber-50/75 px-1 py-0.5 rounded border border-amber-100/50 flex-1 text-center font-extrabold">{pCount} P</span>
                  <span className="text-blue-600 bg-blue-50/75 px-1 py-0.5 rounded border border-blue-100/50 flex-1 text-center font-extrabold">{iCount} I</span>
                  <span className="text-emerald-600 bg-emerald-50/75 px-1 py-0.5 rounded border border-emerald-100/50 flex-1 text-center font-extrabold">{sCount} S</span>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Charts Grid */}
      <div className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 text-left">
          {/* Chart 1: New Sign-ups */}
          {hasPermission('MANAGE_USERS') && (() => {
            const signUpData = getUserSignUpTrendData();
            const maxSignUps = Math.max(...signUpData.map(d => d.count), 5);
            const N = signUpData.length;
            const points = signUpData.map((d, i) => ({
              x: N > 1 ? 60 + (i * (350 / (N - 1))) : 235,
              y: 180 - (d.count / maxSignUps) * 140
            }));
            const linePath = N > 0 ? points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') : '';
            const areaPath = N > 0 ? `${linePath} L ${points[points.length - 1].x} 180 L ${points[0].x} 180 Z` : '';

            return (
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm relative">
                <div className="flex justify-between items-center mb-6">
                  <div className="text-left">
                    <h3 className="text-base font-extrabold text-gray-800 text-left">New Sign-ups</h3>
                    <p className="text-xs text-gray-400 font-semibold mt-0.5 text-left">
                      {overviewPreset === 'all' || !overviewPreset ? 'Monthly user registration signup rate' : 'Dynamic signup trend rate'}
                    </p>
                  </div>
                  <span className="text-[10px] font-bold text-violet-600 bg-violet-50 px-2 py-0.5 rounded border border-violet-100 uppercase tracking-wider">
                    Sign-ups count
                  </span>
                </div>

                <div className="relative h-[220px] w-full">
                  <svg className="w-full h-full" viewBox="0 0 450 220" preserveAspectRatio="xMidYMid meet">
                    <defs>
                      <linearGradient id="signUpAreaGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.0" />
                      </linearGradient>
                    </defs>

                    <line x1="55" y1="40" x2="430" y2="40" stroke="#f3f4f6" strokeDasharray="3 3" />
                    <line x1="55" y1="110" x2="430" y2="110" stroke="#f3f4f6" strokeDasharray="3 3" />
                    <line x1="55" y1="180" x2="430" y2="180" stroke="#e5e7eb" strokeWidth="1.5" />

                    <text x="45" y="44" className="text-[9px] font-bold text-gray-400" textAnchor="end">{maxSignUps}</text>
                    <text x="45" y="114" className="text-[9px] font-bold text-gray-400" textAnchor="end">{Math.ceil(maxSignUps / 2)}</text>
                    <text x="45" y="184" className="text-[9px] font-bold text-gray-400" textAnchor="end">0</text>

                    {N > 1 && <path d={areaPath} fill="url(#signUpAreaGrad)" />}
                    {N > 1 && <path d={linePath} fill="none" stroke="#8b5cf6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />}

                    {points.map((p, idx) => (
                      <g key={idx}>
                        <circle
                          cx={p.x}
                          cy={p.y}
                          r={hoveredSignUpIndex === idx ? "7" : "5"}
                          fill="#8b5cf6"
                          stroke="white"
                          strokeWidth="2"
                          className="cursor-pointer transition-all duration-155"
                          onMouseEnter={() => setHoveredSignUpIndex(idx)}
                          onMouseLeave={() => setHoveredSignUpIndex(null)}
                        />
                        {(() => {
                          let showLabel = true;
                          if (N > 15 && N <= 31) {
                            showLabel = idx % 4 === 0 || idx === N - 1;
                          } else if (N > 7 && N <= 15) {
                            showLabel = idx % 2 === 0 || idx === N - 1;
                          } else if (N > 31) {
                            showLabel = idx % 6 === 0 || idx === N - 1;
                          }
                          return showLabel ? (
                            <text
                              x={p.x}
                              y="202"
                              className="text-[10px] font-bold text-gray-400"
                              textAnchor="middle"
                            >
                              {signUpData[idx].name}
                            </text>
                          ) : null;
                        })()}
                      </g>
                    ))}
                  </svg>

                  {hoveredSignUpIndex !== null && hoveredSignUpIndex < N && (
                    <div
                      className="absolute bg-slate-900 text-white rounded-xl p-2.5 shadow-xl text-xs font-bold pointer-events-none z-30 transition-all border border-slate-800"
                      style={{
                        left: `${(points[hoveredSignUpIndex].x / 450) * 100}%`,
                        top: `${(points[hoveredSignUpIndex].y / 220) * 100 - 25}%`,
                        transform: 'translate(-50%, -100%)'
                      }}
                    >
                      <div className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">
                        {signUpData[hoveredSignUpIndex].name} {signUpData[hoveredSignUpIndex].year || ''}
                      </div>
                      <div className="text-sm font-extrabold text-violet-300 mt-0.5">
                        {signUpData[hoveredSignUpIndex].count} Sign-up(s)
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Chart 2: New Listings Volume */}
          {hasPermission('APPROVE_LISTINGS') && (() => {
            const listingData = getListingTrendData();
            const maxListings = Math.max(...listingData.map(d => d.count), 5);
            const N = listingData.length;
            const spacing = N > 1 ? 350 / (N - 1) : 350;
            const barWidth = Math.max(4, Math.min(26, spacing * 0.6));
            const points = listingData.map((d, i) => {
              const x = N > 1 ? 55 + (i * spacing) : 230;
              const h = (d.count / maxListings) * 140;
              return {
                x: x - barWidth / 2,
                y: 180 - h,
                h: h,
                count: d.count,
                name: d.name
              };
            });

            return (
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm relative">
                <div className="flex justify-between items-center mb-6">
                  <div className="text-left">
                    <h3 className="text-base font-extrabold text-gray-800 text-left">New Listings Volume</h3>
                    <p className="text-xs text-gray-400 font-semibold mt-0.5 text-left">
                      {overviewPreset === 'all' || !overviewPreset ? 'Monthly vendor registration signup rate' : 'Dynamic signup trend rate'}
                    </p>
                  </div>
                  <span className="text-[10px] font-bold text-indigo-650 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100 uppercase tracking-wider">
                    Growth rate
                  </span>
                </div>

                <div className="relative h-[220px] w-full">
                  <svg className="w-full h-full" viewBox="0 0 450 220" preserveAspectRatio="xMidYMid meet">
                    <defs>
                      <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#818cf8" />
                        <stop offset="100%" stopColor="#4f46e5" />
                      </linearGradient>
                    </defs>

                    <line x1="55" y1="40" x2="430" y2="40" stroke="#f3f4f6" strokeDasharray="3 3" />
                    <line x1="55" y1="110" x2="430" y2="110" stroke="#f3f4f6" strokeDasharray="3 3" />
                    <line x1="55" y1="180" x2="430" y2="180" stroke="#e5e7eb" strokeWidth="1.5" />

                    <text x="45" y="44" className="text-[9px] font-bold text-gray-400" textAnchor="end">{maxListings}</text>
                    <text x="45" y="114" className="text-[9px] font-bold text-gray-400" textAnchor="end">{Math.ceil(maxListings / 2)}</text>
                    <text x="45" y="184" className="text-[9px] font-bold text-gray-400" textAnchor="end">0</text>

                    {points.map((p, idx) => (
                      <g key={idx}>
                        <rect
                          x={p.x}
                          y={p.y}
                          width={barWidth}
                          height={p.h}
                          rx="5"
                          fill={hoveredListingIndex === idx ? "#4f46e5" : "url(#barGrad)"}
                          className="cursor-pointer transition-all duration-155"
                          onMouseEnter={() => setHoveredListingIndex(idx)}
                          onMouseLeave={() => setHoveredListingIndex(null)}
                        />
                        {(() => {
                          let showLabel = true;
                          if (N > 15 && N <= 31) {
                            showLabel = idx % 4 === 0 || idx === N - 1;
                          } else if (N > 7 && N <= 15) {
                            showLabel = idx % 2 === 0 || idx === N - 1;
                          } else if (N > 31) {
                            showLabel = idx % 6 === 0 || idx === N - 1;
                          }
                          return showLabel ? (
                            <text
                              x={p.x + barWidth / 2}
                              y="202"
                              className="text-[10px] font-bold text-gray-400"
                              textAnchor="middle"
                            >
                              {p.name}
                            </text>
                          ) : null;
                        })()}
                      </g>
                    ))}
                  </svg>

                  {hoveredListingIndex !== null && hoveredListingIndex < N && (
                    <div
                      className="absolute bg-slate-955 text-white rounded-xl p-2.5 shadow-xl text-xs font-bold pointer-events-none z-30 transition-all border border-slate-800"
                      style={{
                        left: `${((points[hoveredListingIndex].x + barWidth / 2) / 450) * 100}%`,
                        top: `${(points[hoveredListingIndex].y / 220) * 100 - 25}%`,
                        transform: 'translate(-50%, -100%)'
                      }}
                    >
                      <div className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">
                        {listingData[hoveredListingIndex].name} {listingData[hoveredListingIndex].year || ''}
                      </div>
                      <div className="text-sm font-extrabold text-indigo-300 mt-0.5">
                        {points[hoveredListingIndex].count} Listing(s)
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Chart 3: Sales & Revenue Trend (Full Width) */}
        {hasPermission('MANAGE_MEMBERSHIPS') && (() => {
          const revenueData = getRevenueTrendData();
          const maxRevenue = Math.max(...revenueData.map(d => d.revenue), 10000);
          const N = revenueData.length;
          const points = revenueData.map((d, i) => ({
            x: N > 1 ? 60 + (i * (350 / (N - 1))) : 235,
            y: 180 - (d.revenue / maxRevenue) * 140
          }));
          const linePath = N > 0 ? points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') : '';
          const areaPath = N > 0 ? `${linePath} L ${points[points.length - 1].x} 180 L ${points[0].x} 180 Z` : '';

          return (
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm relative w-full">
              <div className="flex justify-between items-center mb-6">
                <div className="text-left">
                  <h3 className="text-base font-extrabold text-gray-800 text-left">Sales & Revenue Trend</h3>
                  <p className="text-xs text-gray-400 font-semibold mt-0.5 text-left">
                    {overviewPreset === 'all' || !overviewPreset ? 'Monthly gross revenue overview' : 'Dynamic range revenue overview'}
                  </p>
                </div>
                <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100 uppercase tracking-wider">
                  Live data
                </span>
              </div>

              <div className="relative h-[220px] w-full">
                <svg className="w-full h-full" viewBox="0 0 450 220" preserveAspectRatio="xMidYMid meet">
                  <defs>
                    <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2ab391" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="#2ab391" stopOpacity="0.0" />
                    </linearGradient>
                  </defs>

                  <line x1="55" y1="40" x2="430" y2="40" stroke="#f3f4f6" strokeDasharray="3 3" />
                  <line x1="55" y1="110" x2="430" y2="110" stroke="#f3f4f6" strokeDasharray="3 3" />
                  <line x1="55" y1="180" x2="430" y2="180" stroke="#e5e7eb" strokeWidth="1.5" />

                  <text x="45" y="44" className="text-[9px] font-bold text-gray-400" textAnchor="end">₹{(maxRevenue).toLocaleString()}</text>
                  <text x="45" y="114" className="text-[9px] font-bold text-gray-400" textAnchor="end">₹{(maxRevenue / 2).toLocaleString()}</text>
                  <text x="45" y="184" className="text-[9px] font-bold text-gray-400" textAnchor="end">₹0</text>

                  {N > 1 && <path d={areaPath} fill="url(#areaGrad)" />}
                  {N > 1 && <path d={linePath} fill="none" stroke="#2ab391" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />}

                  {points.map((p, idx) => (
                    <g key={idx}>
                      <circle
                        cx={p.x}
                        cy={p.y}
                        r={hoveredRevenueIndex === idx ? "7" : "5"}
                        fill="#2ab391"
                        stroke="white"
                        strokeWidth="2"
                        className="cursor-pointer transition-all duration-155"
                        onMouseEnter={() => setHoveredRevenueIndex(idx)}
                        onMouseLeave={() => setHoveredRevenueIndex(null)}
                      />
                      {(() => {
                        let showLabel = true;
                        if (N > 15 && N <= 31) {
                          showLabel = idx % 4 === 0 || idx === N - 1;
                        } else if (N > 7 && N <= 15) {
                          showLabel = idx % 2 === 0 || idx === N - 1;
                        } else if (N > 31) {
                          showLabel = idx % 6 === 0 || idx === N - 1;
                        }
                        return showLabel ? (
                          <text
                            x={p.x}
                            y="202"
                            className="text-[10px] font-bold text-gray-400"
                            textAnchor="middle"
                          >
                            {revenueData[idx].name}
                          </text>
                        ) : null;
                      })()}
                    </g>
                  ))}
                </svg>

                {hoveredRevenueIndex !== null && hoveredRevenueIndex < N && (
                  <div
                    className="absolute bg-slate-900 text-white rounded-xl p-2.5 shadow-xl text-xs font-bold pointer-events-none z-30 transition-all border border-slate-800"
                    style={{
                      left: `${(points[hoveredRevenueIndex].x / 450) * 100}%`,
                      top: `${(points[hoveredRevenueIndex].y / 220) * 100 - 25}%`,
                      transform: 'translate(-50%, -100%)'
                    }}
                  >
                    <div className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">
                      {revenueData[hoveredRevenueIndex].name} {revenueData[hoveredRevenueIndex].year || ''}
                    </div>
                    <div className="text-sm font-extrabold text-emerald-400 mt-0.5">
                      ₹{revenueData[hoveredRevenueIndex].revenue.toLocaleString()}
                    </div>
                    <div className="text-[9px] font-medium text-gray-300 mt-0.5">
                      {revenueData[hoveredRevenueIndex].count} Upgrade(s)
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Chart 4: Membership Tier Distribution (Full Width) */}
        <div className="w-full text-left">
          {hasPermission('MANAGE_MEMBERSHIPS') && (() => {
            const gatewayPremiumCount = filteredMembershipsForOverview.filter(m => m.plan_name === 'PREMIUM' && m.status !== 'EXPIRED').length;
            const gatewayProCount = filteredMembershipsForOverview.filter(m => m.plan_name === 'PROFESSIONAL' && m.status !== 'EXPIRED').length;
            const gatewayFreeCount = filteredMembershipsForOverview.filter(m => m.plan_name === 'FREE' && m.status !== 'EXPIRED').length;

            const manualPremiumCount = filteredManualMembershipsForOverview.filter(m => m.plan_name === 'PREMIUM' && !m.is_expired).length;
            const manualProCount = filteredManualMembershipsForOverview.filter(m => m.plan_name === 'PROFESSIONAL' && !m.is_expired).length;

            const individualPremiumCount = filteredMembershipsForOverview.filter(m => !m.is_manual && m.plan_name === 'PREMIUM' && m.status !== 'EXPIRED' && ((m.listing_type || m.listingType) === 'INDIVIDUAL')).length +
              filteredManualMembershipsForOverview.filter(m => m.plan_name === 'PREMIUM' && !m.is_expired && ((m.listing_type || m.listingType) === 'INDIVIDUAL')).length;
            const individualProCount = filteredMembershipsForOverview.filter(m => !m.is_manual && m.plan_name === 'PROFESSIONAL' && m.status !== 'EXPIRED' && ((m.listing_type || m.listingType) === 'INDIVIDUAL')).length +
              filteredManualMembershipsForOverview.filter(m => m.plan_name === 'PROFESSIONAL' && !m.is_expired && ((m.listing_type || m.listingType) === 'INDIVIDUAL')).length;
            const individualFreeCount = filteredMembershipsForOverview.filter(m => m.plan_name === 'FREE' && m.status !== 'EXPIRED' && ((m.listing_type || m.listingType) === 'INDIVIDUAL')).length;

            const premiumCount = gatewayPremiumCount + manualPremiumCount;
            const proCount = gatewayProCount + manualProCount;
            const freeCount = gatewayFreeCount;
            const totalSegments = premiumCount + proCount + freeCount || 1;

            const premiumShare = (premiumCount / totalSegments) * 314.159;
            const proShare = (proCount / totalSegments) * 314.159;
            const freeShare = (freeCount / totalSegments) * 314.159;

            return (
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm w-full">
                <h3 className="text-base font-extrabold text-gray-800 mb-6 text-left">Membership Tier Distribution</h3>

                <div className="flex flex-col sm:flex-row items-center gap-8 sm:gap-12 lg:gap-16">
                  <div className="relative w-[140px] h-[140px] flex-shrink-0">
                    <svg width="140" height="140" viewBox="0 0 140 140" className="transform -rotate-90">
                      <circle cx="70" cy="70" r="50" fill="transparent" stroke="#f3f4f6" strokeWidth="16" />
                      {premiumCount > 0 && (
                        <circle
                          cx="70"
                          cy="70"
                          r="50"
                          fill="transparent"
                          stroke="#7c3aed"
                          strokeWidth="16"
                          strokeDasharray={`${premiumShare} ${314.159 - premiumShare}`}
                          strokeDashoffset="0"
                          className="transition-all duration-500"
                        />
                      )}
                      {proCount > 0 && (
                        <circle
                          cx="70"
                          cy="70"
                          r="50"
                          fill="transparent"
                          stroke="#2ab391"
                          strokeWidth="16"
                          strokeDasharray={`${proShare} ${314.159 - proShare}`}
                          strokeDashoffset={-premiumShare}
                          className="transition-all duration-500"
                        />
                      )}
                      {freeCount > 0 && (
                        <circle
                          cx="70"
                          cy="70"
                          r="50"
                          fill="transparent"
                          stroke="#94a3b8"
                          strokeWidth="16"
                          strokeDasharray={`${freeShare} ${314.159 - freeShare}`}
                          strokeDashoffset={-(premiumShare + proShare)}
                          className="transition-all duration-500"
                        />
                      )}
                    </svg>

                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-xl font-extrabold text-gray-800">{totalSegments === 1 && premiumCount + proCount + freeCount === 0 ? 0 : totalSegments}</span>
                      <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Total Tiers</span>
                    </div>
                  </div>

                  <div className="flex-1 space-y-4 w-full text-left">
                    <div>
                      <div className="flex justify-between items-center text-xs font-bold text-gray-500 mb-1.5">
                        <span className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-[#7c3aed]"></span>
                          Premium Plan
                        </span>
                        <span className="flex items-center gap-2">
                          {manualPremiumCount > 0 && (
                            <span className="text-[9px] text-indigo-600 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded font-semibold">
                              {manualPremiumCount} Manual
                            </span>
                          )}
                          {individualPremiumCount > 0 && (
                            <span className="text-[9px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded font-semibold">
                              {individualPremiumCount} Individual
                            </span>
                          )}
                          <span>{premiumCount} active ({premiumCount + proCount + freeCount === 0 ? 0 : Math.round((premiumCount / totalSegments) * 100)}%)</span>
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                        <div className="bg-[#7c3aed] h-full rounded-full" style={{ width: `${premiumCount + proCount + freeCount === 0 ? 0 : (premiumCount / totalSegments) * 100}%` }}></div>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between items-center text-xs font-bold text-gray-500 mb-1.5">
                        <span className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-[#2ab391]"></span>
                          Professional Plan
                        </span>
                        <span className="flex items-center gap-2">
                          {manualProCount > 0 && (
                            <span className="text-[9px] text-indigo-600 bg-indigo-50 border border-indigo-100 px-1 py-0.5 rounded font-semibold">
                              {manualProCount} Manual
                            </span>
                          )}
                          {individualProCount > 0 && (
                            <span className="text-[9px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded font-semibold">
                              {individualProCount} Individual
                            </span>
                          )}
                          <span>{proCount} active ({premiumCount + proCount + freeCount === 0 ? 0 : Math.round((proCount / totalSegments) * 100)}%)</span>
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                        <div className="bg-[#2ab391] h-full rounded-full" style={{ width: `${premiumCount + proCount + freeCount === 0 ? 0 : (proCount / totalSegments) * 100}%` }}></div>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between items-center text-xs font-bold text-gray-500 mb-1.5">
                        <span className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-[#94a3b8]"></span>
                          Free Plan (₹0)
                        </span>
                        <span className="flex items-center gap-2">
                          {individualFreeCount > 0 && (
                            <span className="text-[9px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded font-semibold">
                              {individualFreeCount} Individual
                            </span>
                          )}
                          <span>{freeCount} active ({premiumCount + proCount + freeCount === 0 ? 0 : Math.round((freeCount / totalSegments) * 100)}%)</span>
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                        <div className="bg-[#94a3b8] h-full rounded-full" style={{ width: `${premiumCount + proCount + freeCount === 0 ? 0 : (freeCount / totalSegments) * 100}%` }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
